<script setup>
import { Menu, MenuButton, MenuItems } from '@headlessui/vue';
import { usePopper } from '@/shared/composables/usePopper.js';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import { usePermissions } from '#modules/auth/composables/usePermissions.js';
import { AppPermissions } from '#modules/auth/constants/permissions.js';

const [trigger, container] = usePopper({
    placement: 'right-start',
    offsetDistance: 8,
});

const { canAll } = usePermissions();
</script>

<template>
    <div
        v-if="canAll([AppPermissions.ManageDomains, AppPermissions.ManageSslCertificates])"
        class="quick-add"
    >
        <Menu
            v-slot="{ open }"
            as="div"
            class="relative"
        >
            <MenuButton
                ref="trigger"
                class="btn-pink-gradient btn-scale relative flex size-8 flex-none cursor-pointer items-center justify-center rounded-full before:rounded-full"
            >
                <CFIcon
                    name="add"
                    size="16"
                    class="text-white"
                />
            </MenuButton>

            <Teleport to="body">
                <div
                    v-if="open"
                    ref="container"
                    class="z-30"
                >
                    <Transition
                        appear
                        enter-active-class="transition duration-200 ease-out"
                        enter-from-class="translate-y-1 opacity-0"
                        enter-to-class="translate-y-0 opacity-100"
                        leave-active-class="transition duration-150 ease-in"
                        leave-from-class="translate-y-0 opacity-100"
                        leave-to-class="translate-y-1 opacity-0"
                    >
                        <MenuItems
                            static
                            class="focus:outline-hidden"
                        >
                            <ul class="popover-list">
                                <li class="text-primary text-xs font-bold">
                                    {{ $t('nav.quick_add') }}
                                </li>
                                <li>
                                    <RouterLink :to="{ name: 'new domain' }">
                                        {{ $t('nav.only_domain') }}
                                    </RouterLink>
                                </li>
                                <li>
                                    <RouterLink :to="{ name: 'new ssl' }">
                                        {{ $t('nav.ssl_certificate') }}
                                    </RouterLink>
                                </li>
                            </ul>
                        </MenuItems>
                    </Transition>
                </div>
            </Teleport>
        </Menu>
    </div>
</template>

<style scoped>
@reference "@css/app.css";

.quick-add {
    @apply relative shrink-0 px-8 py-4 transition-all duration-200 ease-in-out;
}

.popover-list {
    @apply relative flex min-w-40 flex-col divide-y divide-gray-200 bg-white p-3 shadow-lg;

    &::before {
        content: '';
        @apply absolute top-3 -left-1 h-0 w-0 border-y-4 border-r-4 border-solid border-t-transparent border-r-white border-b-transparent;
    }

    li {
        @apply text-primary py-1.5 first:pt-0 first:font-bold last:pb-0;

        a {
            @apply text-primary block;
        }
    }
}
</style>
